import { Directive, HostListener, inject, ViewChild } from '@angular/core';
import { NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  OperatorFunction,
  scan,
  startWith,
  Subject,
  tap,
  withLatestFrom,
} from 'rxjs';
import { changeDeviceView } from 'src/app/common/app.helpers';
import AppValues from 'src/app/common/app.values';
import { PaginationState } from 'src/app/components/shared/pagination/pagination.interface';
import { SelectorOption } from 'src/app/interfaces/form-and-field.interface';
import { SearchParams } from 'src/app/interfaces/search.interface';
import { OrganizationLink } from '../../../swagger-api';
import { setDropdownWidth } from '../../forms.helpers';
import { FormFieldSate } from './form-field.state';
import { INITIAL_PAGINATION_STATE } from './selector-options.helpers';
import { SelectorOptionsService } from './selector-options.service';

@Directive()
export abstract class SuggestionFieldShared extends FormFieldSate {
  protected get inputFocus$(): Observable<string> {
    return this.focus$;
  }

  protected get clicksWithClosedPopup$(): Observable<SearchParams> {
    return this.click$.pipe(
      map((text: string) => ({ ...this.initialSearchParams, searchTerm: text.trim() })),
      filter(() => !this.typehead.isPopupOpen()),
    );
  }

  public get dropdownSelector(): string {
    return (this.typehead && `#${this.popupId}`) || '';
  }

  public initialSearchParams: SearchParams = {
    searchTerm: '',
    pagination: INITIAL_PAGINATION_STATE,
  };

  public focus$: Subject<string> = new Subject();
  public click$: Subject<string> = new Subject();
  public loadMore$: Subject<Partial<SearchParams>> = new Subject();

  public isLoading$: Observable<boolean> = inject(SelectorOptionsService).isLoading;
  public paginationState$: Observable<PaginationState> = inject(SelectorOptionsService).paginationState;
  public isMobileView = false;

  @ViewChild('typehead', { static: false }) protected typehead: NgbTypeahead;

  protected dropdownWidth: number;
  protected inputHorizontalPadding = 25;

  protected optionsService = inject(SelectorOptionsService);
  protected translateService = inject(TranslateService);

  private get popupId(): string | null {
    return this.typehead ? this.typehead.popupId : null;
  }

  @HostListener('click')
  public clickControl(): void {
    setDropdownWidth('.control-container', this.popupId);
  }

  @HostListener('window:resize')
  public resizeWindow(): void {
    if (this.typehead) {
      setDropdownWidth('.control-container', this.popupId);
      this.isMobileView = changeDeviceView(AppValues.DEFAULT_WIDTH_FOR_MOBILE);
    }
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.initSelectedItems();
    this.isMobileView = changeDeviceView(AppValues.DEFAULT_WIDTH_FOR_MOBILE);
  }

  /**
   * @description Sets up the displayed info for result and type of
   *              data on which search suggestions will be searched.
   * @returns { string }
   */
  public formatter = (option: SelectorOption | OrganizationLink | string): string => {
    if (!option) {
      return '';
    }

    if (typeof option === 'string') {
      return this.translateService.instant(option) as string;
    }

    if ('title' in option) {
      return this.translateService.instant(option.title) as string;
    }

    if ('organization_name' in option) {
      return this.translateService.instant(option.organization_name) as string;
    }

    return '';
  };

  /**
   * @description Calls on ngbTypeahead input event.
   *              Hides and filters the search suggestions list.
   * @returns { Observable<SelectorOption[]> || Observable<OrganizationLink[]> }
   */
  public search = (text$: Observable<string>): Observable<SelectorOption[] | OrganizationLink[]> =>
    merge(
      this.updateSearchParams$(text$),
      this.clicksWithClosedPopup$,
      this.loadMore$.pipe(
        withLatestFrom(text$.pipe(startWith(''))),
        map<[Partial<SearchParams>, string], SearchParams>(
          ([paginationParams, text]: [Partial<SearchParams>, string]) => ({
            pagination: paginationParams.pagination,
            searchTerm: text,
          }),
        ),
      ),
    ).pipe(
      scan((prev: SearchParams, curr: SearchParams) => ({ ...prev, ...curr })),
      this.getOptionsOperatorFunction(),
      withLatestFrom(text$.pipe(startWith(''))),
      map(([items, text]: [SelectorOption[] | OrganizationLink[], string]) => this.sortResults(items, text)),
      tap(() => setTimeout(() => setDropdownWidth('.control-container', this.popupId))),
    );

  public onLoadMore({ currentPage, totalItemsCount }: PaginationState): void {
    this.loadMore$.next({
      pagination: { totalItemsCount, currentPage: ++currentPage },
    });
  }

  public abstract checkErrors(event?: NgbTypeaheadSelectItemEvent): void;

  protected updateSearchParams$(text$: Observable<string>): Observable<SearchParams> {
    return text$.pipe(
      map((text: string) => ({ ...this.initialSearchParams, searchTerm: text.trim() })),
      debounceTime(AppValues.DEBOUNCE_TIME),
      distinctUntilChanged(),
    );
  }

  protected sortResults(
    items: SelectorOption[] | OrganizationLink[],
    searchTerm = '',
  ): SelectorOption[] | OrganizationLink[] {
    if (!Array.isArray(items)) {
      return items;
    }

    const normalizedSearch: string = searchTerm.toString().trim().toLowerCase();

    return items.slice().sort((a: SelectorOption | OrganizationLink, b: SelectorOption | OrganizationLink) => {
      const leftRaw = this.formatter(a).toString().trim();
      const rightRaw = this.formatter(b).toString().trim();

      const left = leftRaw.toLowerCase();
      const right = rightRaw.toLowerCase();

      // If we have a search term, prioritize "startsWith"
      if (normalizedSearch) {
        const leftStarts = left.startsWith(normalizedSearch);
        const rightStarts = right.startsWith(normalizedSearch);

        if (leftStarts && !rightStarts) return -1;
        if (!leftStarts && rightStarts) return 1;
      }

      // Fallback to your original locale-aware sort
      return leftRaw.localeCompare(rightRaw, undefined, {
        sensitivity: 'base',
        numeric: true,
      });
    });
  }

  protected abstract getOptionsOperatorFunction(): OperatorFunction<
    SearchParams,
    SelectorOption[] | OrganizationLink[]
  >;

  protected abstract initSelectedItems(): void;

  protected abstract onControlDisabled(): void;

  protected override onNullControlValue(): void {
    this.onControlDisabled();
    super.onNullControlValue();
  }

  protected subscribeToControlChanges(): void {
    this.subscriptions.push(
      this.control.statusChanges.pipe(distinctUntilChanged()).subscribe(() => {
        if (this.control.disabled) {
          this.onControlDisabled();
        }
      }),
    );
  }
}
